import { request } from '@ux/util';
import testShoppers from '../enums/shopper-blocklist';

/**
 * Validates cache data
 *
 * @param {Object} cache Cache Data
 * @param {string} cache.shopperId - customer number
 * @param {string} cache.market - current market
 * @param {number} cache.timestamp - when the data was cached
 * @param {string} shopperId Customer identifier
 * @param {string} market Location and language date (e.g. en-US, el-GR, fr-CA)
 *
 * @private
 *
 * @returns {boolean} True if the cache is still valid
 */

function isValid(cache, shopperId, market) {
  return cache
    && cache.shopperId
    && cache.shopperId === shopperId
    && cache.market
    && cache.market === market
    && cache.timestamp
    && (Date.now() - cache.timestamp) < 1000 * 60 * 60 * 24; // 24 hours
}

const cacheSet = 'Notification_Set';
const cacheReq = 'Notification_Req';
const cacheRecv = 'Notification_Get';
const header = 'notify:';

let iframeCache = null;
let getCacheTimer = null;
let getCacheHandler = null;

/**
 * Initializes cache data
 *
 * @param {string} iframeCacheUrl URl
 * @param {string} iframeClasses Added class
 *
 * @private
 */

function initCache(iframeCacheUrl, iframeClasses) {
  iframeCache = document.createElement('iframe');
  iframeCache.src = iframeCacheUrl;
  iframeCache.className = iframeClasses;

  // backwards compatibility with eldorado
  iframeCache.id = 'notificationCacheFrame';
  iframeCache.style.display = 'none';

  document.body.appendChild(iframeCache);
}

/**
 * Posts to cache.
 *
 * @param {object} obj Data to post
 *
 * @private
 */

function postMessage(obj) {
  if (iframeCache && iframeCache.contentWindow) {
    iframeCache.contentWindow.postMessage(header + JSON.stringify(obj), '*');
  }
}

/**
 * Turn cache into object.
 *
 * @param {string | Object} msg Data to be serialized
 *
 * @private
 *
 * @returns {Object} msg Data deserialized
 */

function deserializeMessage(msg) {
  if (typeof msg === 'object') {
    return msg;
  } else if (typeof msg === 'string' && msg.substr(0, header.length) === header) {
    return JSON.parse(msg.substr(header.length));
  }
}

/**
 * Posts notification data to cache.
 *
 * @param {Object} setObj data for notifications cache
 *
 * @private
 */

function setCache(setObj) {
  postMessage({ type: cacheSet, payload: setObj });
}

/**
 * Get cache
 *
 * @param {function} successCb Success callback for when cache is received
 * @param {function} errorCb Error callback for failed cache return
 *
 * @private
 *
 */

function getCache(successCb, errorCb) {
  let timeoutCount = 0;
  getCacheTimer = setTimeout(function cacheTimeout() {
    if (timeoutCount < 1) {
      timeoutCount++;
      postMessage({ type: cacheReq });
      getCacheTimer = setTimeout(cacheTimeout, 1000);
      return;
    }

    getCacheCleanup();
    errorCb();
  }, 1000);

  getCacheHandler = function (e) {
    getCacheCleanup();

    const data = deserializeMessage(e.data);
    if (data && data.type === cacheRecv) {
      return successCb(data.payload);
    }

    errorCb();
  };

  window.addEventListener('message', getCacheHandler, false);
}

/**
 * Cleans up cache by calling 'clearTimeout' and 'removeEventListeners'.
 *
 * @private
 *
 */

function getCacheCleanup() {
  if (getCacheTimer) {
    clearTimeout(getCacheTimer);
    getCacheTimer = null;
  }

  if (getCacheHandler) {
    window.removeEventListener('message', getCacheHandler);
    getCacheHandler = null;
  }
}


/**
 * Request notification if getCache returns error.
 *
 * @param {string} href URl for get request
 * @param {number} privateLabelId The customer's plid
 * @param {string} shopperId Customer identifier
 * @param {string} market Location and language date (e.g. en-US, el-GR, fr-CA)
 * @param {function} successCb Success callback
 * @param {function} errorCb Error callback used when error or response data is not an array
 *
 * @private
 */
// eslint-disable-next-line max-params
function requestNotifications(href, privateLabelId, shopperId, market, successCb, errorCb) {
  // Skip the real request for our test shoppers used for UI automation
  // to avoid overriding the mock request.
  if (testShoppers.indexOf(shopperId) !== -1) {
    return successCb({});
  }

  request.get(href, {
    headers: {
      'Content-Type': 'application/json',
      'X-App-Key': 'customerexperience'
    }
  }, function notifCacheResponseHandler(error, notifications) {
    if (error || !Array.isArray(notifications)) {
      return errorCb(error || new Error(`expected an array but got ${notifications}`));
    }

    const response = {
      notifications,
      shopperId,
      market,
      timestamp: Date.now()
    };
    setCache(response);
    successCb(response);
  });
}

/**
 * Initial get cache function
 *
 * @param {string} cache URL
 * @param {string} iframeClasses Classes to append to the iframe
 * @param {Object} api Stores notifications data urls
 * @param {string} api.href The url from which to fetch notifications
 * @param {number} privateLabelId ID to identify Private Labels
 * @param {string} shopperId Customer identifier
 * @param {string} market Location and language (e.g. en-US, el-GR, fr-CA)
 * @param {function} successCb Success callback
 * @param {function} errorCb Error callback
 *
 * @public
 */

// eslint-disable-next-line max-params
export function get(cache, iframeClasses, api, privateLabelId, shopperId, market, successCb, errorCb) {
  function cacheGetError() {
    requestNotifications(api.href, privateLabelId, shopperId, market, successCb, errorCb);
  }

  function cacheGetSuccess(obj) {
    if (isValid(obj, shopperId, market)) {
      successCb(obj);
      return;
    }

    cacheGetError();
  }

  getCache(cacheGetSuccess, cacheGetError);

  // The cache sends its notifications as soon as it loads, so do this *after* setting up the cache receive handlers in getCache
  if (!iframeCache) {
    initCache(cache, iframeClasses);
  }
}

/**
 * Reset cache by reseting iFrameCache to null.
 *
 * @public
 */

export function reset() {
  if (iframeCache) {
    iframeCache.parentNode.removeChild(iframeCache);
    iframeCache = null;
  }

  getCacheCleanup();
}
